import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { collection, documentId, getDocs, onSnapshot, or, query, where } from 'firebase/firestore';
import { normalizeSteps } from '../../functions/shared/programmeSelection';
import { resolveTicketProgrammeStatus } from '@/lib/ticketProgrammeStatus';

const CHUNK_SIZE = 10;
const messageEntry = (uid, state, label, details) => [{
  key: `${uid}:${state}`, state, label, details, expiresAt: null, fraction: null,
}];

// Share one lookup for every ticket of the same user. Keep programme reads
// bounded to the ticket list instead of loading the whole monitoring report.
export function useTicketProgrammeStatus(db, tickets) {
  const products = ref([]);
  const productsReady = ref(false);
  const productsFailed = ref(false);
  const rowsByUid = ref(new Map());
  const profilesByUid = ref(new Map());
  const loadingUids = ref(new Set());
  const failedUids = ref(new Set());
  const now = ref(new Date());
  const groups = new Set();
  let productsRequest = null;
  let disposed = false;
  let syncTimeout = null;

  const userIds = computed(() => [...new Set(tickets.value
    .map(ticket => String(ticket.userId || '').trim()).filter(Boolean))].sort());

  const ensureProducts = () => {
    if (productsRequest) return;
    productsRequest = getDocs(collection(db, 'products')).then(snapshot => {
      if (disposed) return;
      products.value = snapshot.docs.map(item => ({ ...item.data(), id: item.id }));
      productsReady.value = true;
    }).catch(() => {
      if (!disposed) productsFailed.value = true;
    });
  };

  const applyGroup = group => {
    if (disposed || group.stopped) return;
    if (group.errors.size) {
      group.ids.forEach(uid => failedUids.value.add(uid));
      return;
    }
    group.ids.forEach(uid => failedUids.value.delete(uid));
    if (group.ready.size < 2) return;
    const rows = new Map(group.ids.map(uid => [uid, []]));
    (group.sources.get('programmes') || []).forEach(row => {
      const uid = String(row.uid || row.userUid || '').trim();
      if (rows.has(uid)) rows.get(uid).push(row);
    });
    const profiles = new Map((group.sources.get('profiles') || []).map(user => [user.id, user]));
    rows.forEach((programmes, uid) => {
      rowsByUid.value.set(uid, programmes);
      profilesByUid.value.set(uid, profiles.get(uid) || null);
      loadingUids.value.delete(uid);
    });
  };

  const subscribeGroup = ids => {
    const group = { ids, sources: new Map(), ready: new Set(), errors: new Set(), stops: [], stopped: false };
    groups.add(group);
    ids.forEach(uid => loadingUids.value.add(uid));
    const requests = [
      ['programmes', query(collection(db, 'percorsi'), or(where('uid', 'in', ids), where('userUid', 'in', ids)))],
      ['profiles', query(collection(db, 'users'), where(documentId(), 'in', ids))],
    ];
    for (const [source, request] of requests) {
      try {
        const stop = onSnapshot(request,
          { includeMetadataChanges: true }, snapshot => {
            if (disposed || group.stopped) return;
            group.sources.set(source, snapshot.docs.map(item => {
              const data = item.data();
              return source === 'programmes'
                ? { ...data, id: item.id, steps: normalizeSteps(data.steps).filter(Boolean) }
                : { ...data, id: item.id };
            }));
            // An empty local cache does not prove that the user has no programme.
            if (!snapshot.metadata.fromCache) group.ready.add(source);
            group.errors.delete(source);
            applyGroup(group);
          }, () => {
            group.errors.add(source);
            applyGroup(group);
          });
        group.stops.push(stop);
      } catch {
        group.errors.add(source);
        applyGroup(group);
      }
    }
  };

  const syncSubscriptions = () => {
    syncTimeout = null;
    if (disposed) return;
    const currentIds = new Set(userIds.value);
    for (const group of groups) {
      if (group.ids.every(uid => currentIds.has(uid))) continue;
      group.stopped = true;
      group.stops.forEach(stop => stop());
      groups.delete(group);
      group.ids.forEach(uid => {
        rowsByUid.value.delete(uid);
        profilesByUid.value.delete(uid);
        loadingUids.value.delete(uid);
        failedUids.value.delete(uid);
      });
    }
    const subscribedIds = new Set([...groups].flatMap(group => group.ids));
    const newIds = [...currentIds].filter(uid => !subscribedIds.has(uid));
    if (!newIds.length) return;
    ensureProducts();
    for (let index = 0; index < newIds.length; index += CHUNK_SIZE) {
      subscribeGroup(newIds.slice(index, index + CHUNK_SIZE));
    }
  };

  const stopWatch = watch(userIds, () => {
    if (syncTimeout) clearTimeout(syncTimeout);
    syncTimeout = setTimeout(syncSubscriptions, 50);
  }, { immediate: true });
  const clock = setInterval(() => { now.value = new Date(); }, 60000);

  const statusesByUid = computed(() => {
    const statuses = new Map();
    userIds.value.forEach(uid => {
      const user = profilesByUid.value.get(uid);
      if (productsFailed.value || failedUids.value.has(uid)) {
        statuses.set(uid, messageEntry(uid, 'unavailable', 'Non disponibile', 'Impossibile leggere lo stato del programma. Usa Aggiorna ticket per riprovare.'));
      } else if (profilesByUid.value.has(uid) && user === null) {
        statuses.set(uid, messageEntry(uid, 'review', 'Da verificare', 'Il profilo utente non è disponibile.'));
      } else if (!user || !productsReady.value || loadingUids.value.has(uid) || !rowsByUid.value.has(uid)) {
        statuses.set(uid, messageEntry(uid, 'loading', 'Caricamento…', 'Recupero del programma corrente in corso.'));
      } else {
        try {
          statuses.set(uid, resolveTicketProgrammeStatus({ uid, user, percorsi: rowsByUid.value.get(uid), products: products.value, now: now.value }));
        } catch {
          statuses.set(uid, messageEntry(uid, 'review', 'Da verificare', 'I dati del percorso non permettono di ricostruire il programma corrente. Verifica il profilo.'));
        }
      }
    });
    return statuses;
  });

  onBeforeUnmount(() => {
    disposed = true;
    stopWatch();
    if (syncTimeout) clearTimeout(syncTimeout);
    clearInterval(clock);
    groups.forEach(group => {
      group.stopped = true;
      group.stops.forEach(stop => stop());
    });
    groups.clear();
  });

  return {
    ticketProgrammeStatus: ticket => statusesByUid.value.get(String(ticket.userId || '').trim())
      || messageEntry(ticket.id, 'review', 'Da verificare', 'Utente del ticket non riconoscibile.'),
  };
}
